import { axiosClient } from '../../../library/Service/axiosClient';
import { isSchoolAdmin } from '../../../library/Core/SmartFunctions';
import { globalStateBridge } from '../../../library/Core/GlobalContext';
import { GlobalDispatchEvent } from '../../../library/Core/SmartTypes';

/**
 * GlobalState-backed cache for the management directory filter dropdowns
 * (GET /management-api/v1/directory/institute-filters).
 *
 * Prefetched once at login and served to the Alumni Directory so the page does
 * not block on institute-filters every time it opens. School-admins only.
 * Any miss / prefetch failure → the directory falls back to the live API call
 * (no regression).
 *
 * Backed by globalState.directoryFiltersCache (see GlobalReducer/SmartTypes).
 * `globalDispatch` is optional on the setters — when omitted (e.g. called from
 * a router loader that hasn't been passed it), the module falls back to
 * `globalStateBridge`, which App.tsx keeps in sync with the live
 * globalState/globalDispatch on every render so non-React call sites can still
 * read/write global state without useContext.
 */
export const setDirectoryFiltersCache = (data: any, globalDispatch?: (dispatchEvent: GlobalDispatchEvent) => void) => {
    (globalDispatch ?? globalStateBridge.globalDispatch)({ type: 'SET_DIRECTORY_FILTERS_CACHE', payload: data ?? {} });
};

export const getDirectoryFiltersCache = (globalState?: any): any | null => {
    const cached = (globalState ?? globalStateBridge.globalState)?.directoryFiltersCache;
    // Treat an empty object as a miss so we fall back to the live call.
    return cached && typeof cached === 'object' && Object.keys(cached).length ? cached : null;
};

export const clearDirectoryFiltersCache = (globalDispatch?: (dispatchEvent: GlobalDispatchEvent) => void) => {
    (globalDispatch ?? globalStateBridge.globalDispatch)({ type: 'CLEAR_DIRECTORY_FILTERS_CACHE' });
};

/**
 * Fetch institute-filters and store it. Fire-and-forget safe: never throws, so a
 * slow/failed prefetch can't break login. Gated to school-admins.
 */
export const prefetchDirectoryFilters = async (globalDispatch?: (dispatchEvent: GlobalDispatchEvent) => void) => {
    try {
        if (!isSchoolAdmin()) return;
        const res = await axiosClient().get(
            `${process.env.REACT_APP_STUDENT_MANAGEMENT_SERVER}/directory/institute-filters`
        );
        setDirectoryFiltersCache(res?.data ?? {}, globalDispatch);
    } catch (e) {
        // leave cache empty → directory falls back to the live institute-filters call
    }
};
